import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  Eye,
  Loader2,
  PackageCheck,
  RefreshCw,
  Search,
  ShoppingBag,
  XCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { qrPrintApi } from '../../api/qrPrintApi';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPaginationItems } from '../../utils/qrPrintHelpers';

import '../../styles/QrPrint/QrPrintOrders.css';

const STATUS_OPTIONS = [
  'quotation_requested',
  'negotiating',
  'price_accepted',
  'design_confirmed',
  'in_production',
  'ready',
  'shipped',
  'completed',
  'cancelled',
  'rejected',
];

const translateFallback = (
  t,
  key,
  fallback,
) => {
  const value = t(key);

  return value === key ? fallback : value;
};

const formatStatus = (status) => {
  return String(status || '')
    .replace(/_/g, ' ')
    .trim();
};

const getOrderTotal = (order) => {
  return order.final_total ?? order.estimated_total;
};

const getOrderItemCount = (order) => {
  return order.items?.length || order.items_count || 0;
};


const formatNumber = (value, language) => {
  const number = Number(value || 0);

  try {
    return new Intl.NumberFormat(language).format(number);
  } catch {
    return String(number);
  }
};

const formatOrderMoney = (
  value,
  currency = 'MAD',
  language,
) => {
  const amount = Number(value || 0);
  const safeCurrency = currency || 'MAD';

  try {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency: safeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${safeCurrency}`;
  }
};

function QrPrintStatusSelect({
  value,
  options,
  onChange,
  label,
}) {
  const selectRef = useRef(null);
  const [open, setOpen] = useState(false);

  const selectedOption =
    options.find((option) => option.value === value) ||
    options[0];

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleOutsideClick,
    );
    document.addEventListener(
      'keydown',
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleOutsideClick,
      );
      document.removeEventListener(
        'keydown',
        handleEscape,
      );
    };
  }, [open]);

  return (
    <div
      ref={selectRef}
      className="qr-print-orders-select"
    >
      <button
        type="button"
        className={`qr-print-orders-select__trigger ${
          open ? 'is-open' : ''
        }`}
        onClick={() => setOpen((state) => !state)}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selectedOption?.label || label}</span>

        <ChevronDown
          size={15}
          className={open ? 'is-rotated' : ''}
        />
      </button>

      {open ? (
        <div
          className="qr-print-orders-select__menu"
          role="listbox"
        >
          {options.map((option) => {
            const selected = option.value === value;

            return (
              <button
                key={option.value || 'all'}
                type="button"
                className={selected ? 'is-active' : ''}
                onClick={() => {
                  setOpen(false);
                  onChange(option.value);
                }}
                role="option"
                aria-selected={selected}
              >
                <span>{option.label}</span>

                {selected ? <Check size={14} /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function QrPrintOrders() {
  const { t, language } = useLanguage();

  const direction = String(language || '')
    .toLowerCase()
    .startsWith('ar')
    ? 'rtl'
    : 'ltr';

  const isRtl = direction === 'rtl';

  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');


  const statusOptions = useMemo(
    () => [
      {
        value: '',
        label: translateFallback(
          t,
          'qrPrint.orders.allStatuses',
          'All statuses',
        ),
      },
      ...STATUS_OPTIONS.map((value) => ({
        value,
        label: translateFallback(
          t,
          `qrPrint.statuses.${value}`,
          formatStatus(value),
        ),
      })),
    ],
    [t],
  );

  const summary = useMemo(() => {
    return orders.reduce(
      (totals, order) => {
        const orderStatus = order.status;

        return {
          total: totals.total + 1,
          active:
            totals.active +
            (
              [
                'quotation_requested',
                'negotiating',
                'price_accepted',
                'design_confirmed',
                'in_production',
                'ready',
                'shipped',
              ].includes(orderStatus)
                ? 1
                : 0
            ),
          completed:
            totals.completed +
            (orderStatus === 'completed' ? 1 : 0),
          cancelled:
            totals.cancelled +
            (
              ['cancelled', 'rejected'].includes(orderStatus)
                ? 1
                : 0
            ),
        };
      },
      {
        total: 0,
        active: 0,
        completed: 0,
        cancelled: 0,
      },
    );
  }, [orders]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await qrPrintApi.getOrders({
        search: search.trim() || undefined,
        status: status || undefined,
        locale: language,
        per_page: 100,
      });

      setOrders(getPaginationItems(response));
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          translateFallback(
            t,
            'qrPrint.orders.loadError',
            'Unable to load QR print orders.',
          ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadOrders();
    }, 220);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    status,
    language,
  ]);

  return (
    <main
      className={`qr-print-orders-page ${
        isRtl
          ? 'qr-print-orders-page--rtl'
          : 'qr-print-orders-page--ltr'
      }`}
      dir={direction}
    >
      <header className="qr-print-orders-header">
        <div>
          <span className="qr-print-orders-eyebrow">
            <PackageCheck size={16} />
            {translateFallback(
              t,
              'qrPrint.orders.eyebrow',
              'Production tracking',
            )}
          </span>

          <h1>
            {translateFallback(
              t,
              'qrPrint.orders.title',
              'QR print orders',
            )}
          </h1>

          <p>
            {translateFallback(
              t,
              'qrPrint.orders.description',
              'Follow quotations, negotiations and production progress.',
            )}
          </p>
        </div>

        <button
          type="button"
          className="qr-print-orders-refresh"
          onClick={loadOrders}
          disabled={loading}
        >
          {loading ? (
            <Loader2
              size={17}
              className="qr-print-orders-spin"
            />
          ) : (
            <RefreshCw size={17} />
          )}

          <span>
            {translateFallback(
              t,
              'common.refresh',
              'Refresh',
            )}
          </span>
        </button>
      </header>

      <section className="qr-print-orders-summary">
        <article>
          <span>
            <ShoppingBag size={18} />
          </span>

          <div>
            <strong>{formatNumber(summary.total, language)}</strong>
            <p>
              {translateFallback(
                t,
                'qrPrint.orders.summary.total',
                'Total orders',
              )}
            </p>
          </div>
        </article>

        <article>
          <span>
            <PackageCheck size={18} />
          </span>

          <div>
            <strong>{formatNumber(summary.active, language)}</strong>
            <p>
              {translateFallback(
                t,
                'qrPrint.orders.summary.active',
                'In progress',
              )}
            </p>
          </div>
        </article>

        <article>
          <span>
            <Check size={18} />
          </span>

          <div>
            <strong>{formatNumber(summary.completed, language)}</strong>
            <p>
              {translateFallback(
                t,
                'qrPrint.orders.summary.completed',
                'Completed',
              )}
            </p>
          </div>
        </article>

        <article>
          <span>
            <XCircle size={18} />
          </span>

          <div>
            <strong>{formatNumber(summary.cancelled, language)}</strong>
            <p>
              {translateFallback(
                t,
                'qrPrint.orders.summary.cancelled',
                'Cancelled / rejected',
              )}
            </p>
          </div>
        </article>
      </section>

      {error ? (
        <div className="qr-print-orders-alert">
          {error}
        </div>
      ) : null}

      <section className="qr-print-orders-toolbar">
        <label className="qr-print-orders-search">
          <Search size={18} />

          <input
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
            placeholder={translateFallback(
              t,
              'qrPrint.orders.searchPlaceholder',
              'Search by number, customer, or status...',
            )}
            aria-label={translateFallback(
              t,
              'qrPrint.orders.searchPlaceholder',
              'Search by number, customer, or status...',
            )}
          />
        </label>

        <QrPrintStatusSelect
          value={status}
          options={statusOptions}
          onChange={setStatus}
          label={translateFallback(
            t,
            'qrPrint.orders.allStatuses',
            'All statuses',
          )}
        />
      </section>

      {loading ? (
        <div className="qr-print-orders-loading">
          <Loader2
            size={30}
            className="qr-print-orders-spin"
          />

          <strong>
            {translateFallback(
              t,
              'qrPrint.orders.loading',
              'Loading QR Print orders...',
            )}
          </strong>
        </div>
      ) : orders.length === 0 ? (
        <div className="qr-print-orders-empty">
          <PackageCheck size={34} />

          <h2>
            {translateFallback(
              t,
              'qrPrint.orders.emptyTitle',
              'No QR Print orders',
            )}
          </h2>
        </div>
      ) : (
        <section className="qr-print-orders-list">
          <div className="qr-print-orders-list__head">
            <span>
              {translateFallback(
                t,
                'qrPrint.orders.table.order',
                'Order',
              )}
            </span>

            <span>
              {translateFallback(
                t,
                'qrPrint.orders.table.status',
                'Status',
              )}
            </span>

            <span>
              {translateFallback(
                t,
                'qrPrint.orders.table.total',
                'Total',
              )}
            </span>

            <span>
              {translateFallback(
                t,
                'qrPrint.orders.table.actions',
                'Actions',
              )}
            </span>
          </div>

          {orders.map((order) => (
            <article
              className="qr-print-order-row"
              key={order.id}
            >
              <div className="qr-print-order-main">
                <span
                  className="qr-print-order-number"
                  dir="ltr"
                >
                  {order.order_number}
                </span>

                <h2>{order.establishment?.name || '—'}</h2>

                <p>
                  {formatNumber(
                    getOrderItemCount(order),
                    language,
                  )}{' '}
                  {translateFallback(
                    t,
                    'qrPrint.orders.items',
                    'items',
                  )}
                </p>
              </div>

              <span
                className={`qr-print-status is-${order.status}`}
              >
                {translateFallback(
                  t,
                  `qrPrint.statuses.${order.status}`,
                  formatStatus(order.status),
                )}
              </span>

              <strong
                className="qr-print-order-total"
                dir="ltr"
              >
                {formatOrderMoney(
                  getOrderTotal(order),
                  order.currency || 'MAD',
                  language,
                )}
              </strong>

              <div className="qr-print-order-actions">
                <Link
                  to={`/qr-print/orders/${order.id}`}
                  className="qr-print-order-details"
                  title={translateFallback(
                    t,
                    'qrPrint.orders.viewDetails',
                    'View order details',
                  )}
                >
                  <Eye size={16} />

                  <span>
                    {translateFallback(
                      t,
                      'qrPrint.orders.details',
                      'Details',
                    )}
                  </span>

                  {isRtl ? (
                    <ArrowLeft size={15} />
                  ) : (
                    <ArrowRight size={15} />
                  )}
                </Link>
              </div>
            </article>
          ))}
        </section>
      )}
    </main>
  );
}